import { trackPurchase } from '@/lib/analytics';
import { getErrorMessage } from '@/lib/error-utils';
import { getCreditBalance, getSubscriptionInfo } from '@/server/actions/credit-actions';
import { useAuthInitialized, useIsAuthenticated, useUser } from '@/store/auth-store';
import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

export interface UseSubscriptionProps {
  initialCredits?: {
    availableBalance: number;
    periodTotal: number;
  } | null;
  initialSubscription?: {
    subscriptionId: string;
    planId: string;
    isTrialing: boolean;
    interval: 'month' | 'year';
  } | null;
}

export function useSubscription({
  initialCredits = null,
  initialSubscription = null,
}: UseSubscriptionProps = {}) {
  const isAuthenticated = useIsAuthenticated();
  const user = useUser();
  const router = useRouter();
  const searchParams = useSearchParams();
  const isAuthInitialized = useAuthInitialized();

  // Modal states
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);
  const [isPricingModalOpen, setIsPricingModalOpen] = useState(false);
  const [isTrialModalOpen, setIsTrialModalOpen] = useState(false);
  const toastShownRef = useRef(false);

  // User/Credit states
  const [subscriptionInfo, setSubscriptionInfo] = useState<{
    subscriptionId: string;
    planId: string;
    isTrialing: boolean;
    interval: 'month' | 'year';
  } | null>(initialSubscription);
  const [credits, setCredits] = useState<number | null>(initialCredits?.availableBalance ?? null);
  const [totalCredits, setTotalCredits] = useState<number | null>(
    initialCredits?.periodTotal ?? null
  );

  // Fetch Credits & Subscription
  const refreshCreditsAndSubscription = useCallback(
    async (emitEvent = true) => {
      if (isAuthenticated) {
        try {
          const creditResult = await getCreditBalance();
          if (creditResult.success && creditResult.data) {
            setCredits(creditResult.data.availableBalance);
            setTotalCredits(creditResult.data.periodTotal);
          }
          const subResult = await getSubscriptionInfo();
          if (subResult.success && subResult.data) {
            setSubscriptionInfo({
              subscriptionId: subResult.data.subscriptionId,
              planId: subResult.data.planId,
              isTrialing: subResult.data.isTrialing,
              interval: subResult.data.interval,
            });
          } else {
            setSubscriptionInfo(null);
          }
        } catch (error) {
          console.error('Failed to fetch credits/subscription', getErrorMessage(error));
        } finally {
          // Dispatch event to update Header
          // Only emit if we are the initiator (not responding to an event)
          if (emitEvent) {
            window.dispatchEvent(new Event('credit-update'));
          }
        }
      }
    },
    [isAuthenticated]
  );

  const initialFetchDone = useRef(!!(initialCredits || initialSubscription));

  useEffect(() => {
    if (isAuthenticated && !initialFetchDone.current) {
      refreshCreditsAndSubscription();
      initialFetchDone.current = true;
    }

    // Reset on logout so credits re-fetch on next login
    if (!isAuthenticated) {
      initialFetchDone.current = false;
    }

    // When we hear an update from elsewhere (e.g. Header), refresh ourselves BUT DO NOT re-emit
    const handleCreditUpdate = () => refreshCreditsAndSubscription(false);
    window.addEventListener('credit-update', handleCreditUpdate);

    return () => {
      window.removeEventListener('credit-update', handleCreditUpdate);
    };
  }, [isAuthenticated, refreshCreditsAndSubscription]);

  // Payment Success/Cancel handling
  useEffect(() => {
    const success = searchParams.get('success');
    const canceled = searchParams.get('canceled');

    if (success === 'true' && !toastShownRef.current) {
      toastShownRef.current = true;
      const amountParam = searchParams.get('amount');
      const currency = searchParams.get('currency') || 'GBP';

      if (amountParam) {
        let value = Number(amountParam);
        if (!amountParam.includes('.') && value > 100) {
          value = value / 100;
        }

        const txId = `tx_${Date.now()}`;
        console.log('[Analytics] Usage detected:', { amountParam, value, currency, txId });

        const checkGtag = () => {
          if (typeof window.gtag === 'function') {
            trackPurchase(txId, value, currency);
            return true;
          }
          return false;
        };

        if (!checkGtag()) {
          const interval = setInterval(() => {
            if (checkGtag()) clearInterval(interval);
          }, 500);
          setTimeout(() => clearInterval(interval), 10000);
        }
      }

      toast.success(
        'Payment successful! Your credits are being processed and will appear shortly.'
      );

      const refreshCredits = async () => {
        if (isAuthenticated) {
          try {
            const result = await getCreditBalance();
            if (result.success && result.data) {
              setCredits(result.data.availableBalance);
            }
          } catch (error) {
            console.error('Failed to refresh credits', getErrorMessage(error));
          }
        }
      };
      refreshCredits();

      setTimeout(() => {
        const newUrl = window.location.pathname;
        window.history.replaceState({}, '', newUrl);
      }, 2000);
    } else if (canceled === 'true' && !toastShownRef.current) {
      toast.success('Payment cancelled.');
      toastShownRef.current = true;
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }

    const error = searchParams.get('error');
    if (error) {
      let errorMessage = 'Payment failed. Please try again.';
      switch (error) {
        case 'insufficient_funds':
          errorMessage = 'Payment failed: Insufficient funds.';
          break;
        case 'card_declined':
          errorMessage = 'Payment failed: Card declined.';
          break;
        case 'lost_card':
          errorMessage = 'Payment failed: Card reported lost.';
          break;
      }
      toast.error(errorMessage);
      router.replace('/sora-2-ai-video-generator');
    }
  }, [searchParams, isAuthenticated, router]);

  return {
    isAuthenticated,
    user,
    isSignupModalOpen,
    setIsSignupModalOpen,
    isPricingModalOpen,
    setIsPricingModalOpen,
    isTrialModalOpen,
    setIsTrialModalOpen,
    subscriptionInfo,
    credits,
    totalCredits,
    setCredits,
    refreshCreditsAndSubscription,
    isAuthInitialized,
  };
}
